import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { PageNotFoundComponent } from "./core/components/page-not-found/page-not-found.component";
import { SelectivePreloadingStrategy } from "./core/strategies/selective-preloading.strategy";
import { AuthGuard } from "./pages/users/guards/auth.guard";
import { PageRestrictGuard } from "./pages/users/guards/page-restrict.guard";
import { PermissionGuard } from "./pages/users/guards/permission.guard";
import { PermissionIntero } from "./refact/shared/enums/permission-intero.enum";

const routes: Routes = [
  {
    path: "login",
    loadChildren: () =>
      import("./pages/users/components/auth/auth.module").then(
        (m) => m.AuthModule
      ),
    canActivate: [PageRestrictGuard],
  },
  {
    path: "novologin",
    loadChildren: () =>
      import("./pages/users/components/auth/auth.module").then(
        (m) => m.AuthModule
      ),
    canActivate: [PageRestrictGuard],
  },
  {
    path: "alteraempresarede",
    loadChildren: () =>
      import("./pages/users/components/auth/auth.module").then(
        (m) => m.AuthModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.ALTERAR_EMPRESA_REDE },
  },
  {
    path: "",
    redirectTo: "/home",
    pathMatch: "full",
  },
  {
    path: "live-desktop",
    redirectTo: "/home",
    pathMatch: "full",
  },
  {
    path: "lgpd",
    loadChildren: () =>
      import("./pages/lgpd/lgpd.module").then((m) => m.LgpdModule),

    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.LGPD_PAGE },
  },

  // Refact
  {
    path: "home",
    loadChildren: () =>
      import("./refact/pages/components/home/home.module").then(
        (m) => m.HomeModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    // A permissão correta do Íntegro é 255, porém por segurança deixamos zerado para que nenhum cliente fique sem acesso
    data: { permissionIntero: PermissionIntero.HOME, preload: true },
  },
  {
    path: "bimoda",
    redirectTo: "/minha-empresa",
    pathMatch: "full",
  },
  {
    path: "minha-empresa",
    loadChildren: () =>
      import("./refact/pages/components/dashboard/dashboard.module").then(
        (m) => m.DashboardModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.MINHA_EMPRESA, preload: true },
  },
  {
    path: "relatorios",
    loadChildren: () =>
      import("./refact/pages/components/reports/reports.module").then(
        (m) => m.ReportsModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.RELATORIOS, preload: true },
  },
  {
    path: "acompanhamento-de-metas",
    loadChildren: () =>
      import(
        "./refact/pages/components/goal-tracking/goal-tracking.module"
      ).then((m) => m.GoalTrackingModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.ACOMPANHAMENTO_METAS },
  },
  {
    path: "pedido-a-fornecedores",
    loadChildren: () =>
      import(
        "./refact/pages/components/pedido-a-fornecedores/pedido-a-fornecedores.module"
      ).then((m) => m.PedidoAFornecedoresModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.PEDIDO_FORNECEDORES },
  },
  {
    path: "importacao-xml",
    loadChildren: () =>
      import(
        "./refact/pages/components/importacao-xml/importacao-xml.module"
      ).then((m) => m.ImportacaoXmlModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.IMPORTACAO_XML },
  },
  {
    path: "pedidos-de-venda",
    loadChildren: () =>
      import("./refact/pages/components/sales-order/sales-order.module").then(
        (m) => m.SalesOrderModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.PEDIDOS_VENDA },
  },
  {
    path: "consulta-de-vendas",
    loadChildren: () =>
      import("./refact/pages/components/sales-query/sales-query.module").then(
        (m) => m.SalesQueryModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONSULTA_VENDAS },
  },
  {
    path: "configuracoes-do-ecommerce",
    loadChildren: () =>
      import(
        "./refact/pages/components/configuracoes-ecommerce/configuracoes-ecommerce.module"
      ).then((m) => m.ConfiguracoesEcommerceModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONFIGURACOES_ECOMMERCE },
  },
  {
    path: "painel-monitoramento-ecommerce",
    loadChildren: () =>
      import(
        "./refact/pages/components/painel-monitoramento-ecommerce/painel-monitoramento-ecommerce.module"
      ).then((m) => m.PainelMonitoramentoEcommerceModule),
    canActivate: [AuthGuard],
  },
  {
    path: "cadastro-de-comissoes",
    loadChildren: () =>
      import(
        "./refact/pages/components/commission-registration/commission-registration.module"
      ).then((m) => m.CommissionRegistrationModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CADASTRO_COMISSOES },
  },
  {
    path: "grupo-de-metas",
    loadChildren: () =>
      import("./refact/pages/components/goal-group/goal-group.module").then(
        (m) => m.GoalGroupModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.GRUPO_METAS },
  },
  {
    path: "clientes",
    loadChildren: () =>
      import("./refact/pages/components/clientes/clientes.module").then(
        (m) => m.ClientesModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CLIENTES },
  },
  {
    path: "terminais",
    loadChildren: () =>
      import("./refact/pages/components/terminais/terminais.module").then(
        (m) => m.TerminaisModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TERMINAIS },
  },
  {
    path: "configuracao-inicial",
    loadComponent: () =>
      import("./refact/pages/components/configuracao-inicial/configuracao-inicial.component").then(
        (m) => m.ConfiguracaoInicialComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: "envio-certificado",
    loadComponent: () =>
      import("./refact/pages/components/configuracao-inicial/configuracao-certificado/configuracao-certificado.component").then(
        (m) => m.ConfiguracaoCertificadoComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: "redes",
    loadChildren: () =>
      import("./refact/pages/components/redes/redes.module").then(
        (m) => m.RedesModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.REDES },
  },
  {
    path: "lancamento-de-caixa",
    loadChildren: () =>
      import(
        "./refact/pages/components/registro-caixa-despesas/registro-caixa-despesas.module"
      ).then((m) => m.RegistroCaixaDespesasModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.LANCAMENTO_CAIXA },
  },
  {
    path: "percentual-dia",
    loadChildren: () =>
      import(
        "./refact/pages/components/percentage-day/percentage-day.module"
      ).then((m) => m.PercentageDayModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.PERCENTUAL_DIA },
  },
  {
    path: "percentual-de-comissao",
    loadChildren: () =>
      import(
        "./refact/pages/components/commission-percentage/commission-percentage.module"
      ).then((m) => m.CommissionPercentageModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.PERCENTUAL_COMISSAO },
  },
  {
    path: "lancamento-de-metas",
    loadChildren: () =>
      import(
        "./refact/pages/components/lancamento-metas/lancamento-metas.module"
      ).then((m) => m.LancamentoMetasModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.LANCAMENTO_METAS },
  },
  {
    path: "transferencias",
    loadChildren: () =>
      import(
        "./refact/pages/components/transferencias/transferencias.module"
      ).then((m) => m.TransferenciasModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TRANSFERENCIAS },
  },
  {
    path: "configuracoes-das-lojas",
    loadChildren: () =>
      import(
        "./refact/pages/components/configuracoes-das-lojas/configuracoes-das-lojas.module"
      ).then((m) => m.ConfiguracoesDasLojasModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONFIGURACOES_LOJAS },
  },
  {
    path: "historico-de-produtos",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/historico-de-produtos/historico-de-produtos.module"
      ).then((m) => m.HistoricoDeProdutosModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.HISTORICO_PRODUTOS },
  },
  {
    path: "inventarios",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/inventarios/inventarios.module"
      ).then((m) => m.InventariosModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.INVENTARIOS },
  },
  {
    path: "divergencias",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/inventarios/divergencias/divergencias.module"
      ).then((m) => m.DivergenciasModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.DIVERGENCIAS },
  },
  {
    path: "fornecedores",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/fornecedores/fornecedores.module"
      ).then((m) => m.FornecedoresModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.FORNECEDORES },
  },
  {
    path: "distribuicao",
    loadChildren: () =>
      import("./refact/pages/components/distribuicao/distribuicao.module").then(
        (m) => m.DistribuicaoModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.DISTRIBUICAO },
  },
  {
    path: "tabela-de-precos",
    loadChildren: () =>
      import(
        "./refact/pages/components/tabela-de-precos/tabela-de-precos.module"
      ).then((m) => m.TabelaDePrecosModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TABELA_PRECOS },
  },
  {
    path: "tipos-de-desconto",
    loadChildren: () =>
      import(
        "./refact/pages/components/tipos-desconto/tipos-desconto.module"
      ).then((m) => m.TiposDescontoModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TIPOS_DESCONTO },
  },
  {
    path: "cadastro-de-bancos",
    loadChildren: () =>
      import(
        "./refact/pages/components/cadastro-de-bancos/cadastro-de-bancos.module"
      ).then((m) => m.CadastroDeBancosModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.BANCOS },
  },
  {
    path: "cadastro-de-embalagens",
    loadChildren: () =>
      import(
        "./refact/pages/components/cadastro-de-embalagens/cadastro-de-embalagens.module"
      ).then((m) => m.CadastroDeEmbalagensModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.EMBALAGENS },
  },
  {
    path: "centro-de-custos",
    loadChildren: () =>
      import(
        "./refact/pages/components/centro-de-custos/centro-de-custos.module"
      ).then((m) => m.CentroDeCustosModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CENTRO_CUSTOS },
  },
  {
    path: "tipo-de-notas",
    loadChildren: () =>
      import("./refact/pages/components/tipo-de-nota/tipo-de-nota.module").then(
        (m) => m.TipoDeNotaModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TIPO_NOTAS },
  },
  {
    path: "exportacao-fiscal",
    loadChildren: () =>
      import(
        "./refact/pages/components/exportacao-fiscal/exportacao-fiscal.module"
      ).then((m) => m.ExportacaoFiscalModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.EXPORTACAO_FISCAL },
  },
  {
    path: "batimento-transferencias",
    loadChildren: () =>
      import(
        "./refact/pages/components/transfer-checking/transfer-checking.module"
      ).then((m) => m.TransferCheckingModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.BATIMENTO_TRANSFERENCIAS },
  },
  {
    path: "incluir-transferencias",
    loadChildren: () =>
      import("./refact/pages/components/add-transfer/add-transfer.module").then(
        (m) => m.AddTransferModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.INCLUIR_TRANSFERENCIAS },
  },
  {
    path: "entrada-de-notas-antigo",
    loadChildren: () =>
      import("./refact/pages/components/note-entry/note-entry.module").then(
        (m) => m.NoteEntryModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.ENTRADA_NOTAS_ANTIGO },
  },

  {
    path: "entrada-de-notas",
    loadChildren: () =>
      import(
        "./refact/pages/components/entrada-de-notas/entrada-de-notas.module"
      ).then((m) => m.EntradaDeNotasModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.ENTRADA_NOTAS },
  },
  {
    path: "ncm",
    loadChildren: () =>
      import("./refact/pages/components/ncm/ncm.module").then(
        (m) => m.NcmModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.NCM },
  },
  {
    path: "classificacao-de-clientes",
    loadChildren: () =>
      import(
        "./refact/pages/components/cliente-grupo/cliente-grupo.module"
      ).then((m) => m.ClienteGrupoModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CLASSIFICACAO_CLIENTES },
  },
  {
    path: "profissoes",
    loadChildren: () =>
      import("./refact/pages/components/profissoes/profissoes.module").then(
        (m) => m.ProfissoesModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.PROFISSOES },
  },
  {
    path: "tabelas-de-tributacao",
    loadChildren: () =>
      import("./refact/pages/components/tributos/tributos.module").then(
        (m) => m.TributosModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TABELAS_TRIBUTACAO },
  },
  {
    path: "aliquotas-interestaduais",
    loadChildren: () =>
      import(
        "./refact/pages/components/aliquotas-interestaduais/aliquotas-interestaduais.module"
      ).then((m) => m.AliquotasInterestaduaisModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.ALIQUOTAS_INTERESTADUAIS },
  },
  {
    path: "configuracoes",
    loadChildren: () =>
      import("./refact/pages/components/settings/settings.module").then(
        (m) => m.SettingsModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONFIGURACOES },
  },
  {
    path: "configuracoes/editar",
    loadChildren: () =>
      import("./refact/pages/components/settings/system/system.module").then(
        (m) => m.SystemModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONFIGURACOES_EDITAR },
  },
  {
    path: "configuracoes/integracoes",
    loadChildren: () =>
      import("./refact/pages/components/integracoes/integracoes.module").then(
        (m) => m.IntegracoesModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONFIGURACOES_INTEGRACOES },
  },
  {
    path: "configuracoes/integracoes/alterdata-pay",
    loadChildren: () =>
      import(
        "./refact/pages/components/integracoes/components/alterdata-pay/alterdata-pay.module"
      ).then((m) => m.AlterdataPayModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.ALTERDATA_PAY },
  },
  {
    path: "configuracoes/integracoes/bimer",
    loadChildren: () =>
      import(
        "./refact/pages/components/integracoes/components/bimer/bimer.module"
      ).then((m) => m.BimerModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.BIMER },
  },
  {
    path: "configuracoes/integracoes/bluve",
    loadChildren: () =>
      import(
        "./refact/pages/components/integracoes/components/bluve/bluve.module"
      ).then((m) => m.BluveModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.BLUVE },
  },
  {
    path: "configuracoes/integracoes/nf-stock",
    loadChildren: () =>
      import(
        "./refact/pages/components/integracoes/components/nf-stock/nf-stock.module"
      ).then((m) => m.NfStockModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.NF_STOCK },
  },
  {
    path: "configuracoes/integracoes/webhook",
    loadChildren: () =>
      import(
        "./refact/pages/components/integracoes/components/webhook/webhook.module"
      ).then((m) => m.WebhookModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.WEBHOOK },
  },
  {
    path: "configuracoes/integracoes/ecommerce",
    loadChildren: () =>
      import(
        "./refact/pages/components/integracoes/components/ecommerce/ecommerce.module"
      ).then((m) => m.EcommerceModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.ECOMMERCE },
  },
  {
    path: "configuracoes/integracoes/napp-solutions",
    loadChildren: () =>
      import(
        "./refact/pages/components/integracoes/components/napp-solutions/napp-solutions.module"
      ).then((m) => m.NappSolutionsModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.NAPP_SOLUTIONS },
  },
  {
    path: "configuracoes/integracoes/logs",
    loadChildren: () =>
      import(
        "./refact/pages/components/integracoes/components/logs/logs.module"
      ).then((m) => m.LogsModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.LOGS_INTEGRACOES },
  },
  {
    path: "tipos-de-clientes",
    loadChildren: () =>
      import(
        "./refact/pages/components/tipos-de-clientes/tipos-de-clientes.module"
      ).then((m) => m.TiposDeClientesModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TIPOS_CLIENTES },
  },
  {
    path: "transportadoras",
    loadChildren: () =>
      import("./refact/pages/components/carriers/carriers.module").then(
        (m) => m.CarriersModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TRANSPORTADORAS },
  },
  {
    path: "tipos-de-mensagens",
    loadChildren: () =>
      import("./refact/pages/components/messages/messages.module").then(
        (m) => m.MessagesModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TIPOS_MENSAGENS },
  },
  {
    path: "tipos-de-codigo",
    loadChildren: () =>
      import(
        "./refact/pages/components/tipos-de-codigo/tipos-de-codigo.routing.module"
      ).then((m) => m.TiposDeCodigoRoutingModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TIPOS_DE_CODIGO },
  },
  {
    path: "emprestimos",
    loadChildren: () =>
      import(
        "./refact/pages/components/financial-loan/financial-loan.module"
      ).then((m) => m.FinancialLoanModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.EMPRESTIMOS },
  },
  {
    path: "gestor-de-notas",
    loadChildren: () =>
      import("./refact/pages/components/note-manager/note-manager.module").then(
        (m) => m.NoteManagerModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.GESTOR_NOTAS },
  },
  {
    path: "kiosk",
    loadChildren: () =>
      import("./refact/pages/components/kiosk/kiosk.module").then(
        (m) => m.KioskModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: "pdvup",
    loadChildren: () =>
      import(
        "./refact/pages/components/point-of-sale/point-of-sale.module"
      ).then((m) => m.PointOfSaleModule),
    data: {
      tipoVenda: "PDV",
      titulo: "PdvUP",
      subtitulo: "Faça o lançamento de suas vendas de forma rápida e fácil",
      permissionIntero: PermissionIntero.PDVUP,
      preload: true,
    },
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: "netshopper",
    loadChildren: () =>
      import(
        "./refact/pages/components/point-of-sale/point-of-sale.module"
      ).then((m) => m.PointOfSaleModule),
    data: {
      tipoVenda: "PEDIDO",
      titulo: "NetShopper",
      subtitulo: "Faça o lançamento de seus pedidos de forma rápida e fácil",
      permissionIntero: PermissionIntero.NETSHOPPER,
    },
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: "intero-link",
    loadChildren: () =>
      import(
        "./refact/pages/components/point-of-sale/point-of-sale.module"
      ).then((m) => m.PointOfSaleModule),
    data: {
      tipoVenda: "PEDIDO",
      titulo: "Íntero Pedido Fácil",
      subtitulo: "Faça o lançamento de seus pedidos de forma rápida e fácil",
    },
    canActivate: [AuthGuard],
  },
  {
    path: "gestor-de-pedidos",
    loadChildren: () =>
      import(
        "./refact/pages/components/order-manager/order-manager.module"
      ).then((m) => m.OrderManagerModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.GESTOR_PEDIDOS },
  },
  {
    path: "configuracoes/cadastro-geral",
    loadChildren: () =>
      import(
        "./refact/pages/components/general-register/general-register.module"
      ).then((m) => m.GeneralRegisterModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CADASTRO_GERAL },
  },
  {
    path: "batimento-transferencias",
    loadChildren: () =>
      import(
        "./refact/pages/components/transfer-checking/transfer-checking.module"
      ).then((m) => m.TransferCheckingModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.BATIMENTO_TRANSFERENCIAS },
  },
  {
    path: "incluir-transferencias",
    loadChildren: () =>
      import("./refact/pages/components/add-transfer/add-transfer.module").then(
        (m) => m.AddTransferModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.INCLUIR_TRANSFERENCIAS },
  },
  {
    path: "etiquetas",
    loadChildren: () =>
      import("./refact/pages/components/labels/labels.module").then(
        (m) => m.LabelsModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.ETIQUETAS },
  },
  {
    path: "configuracoes/debug",
    loadChildren: () =>
      import("./refact/pages/components/settings/debug/debug.module").then(
        (m) => m.DebugModule
      ),
  },
  {
    path: "configuracoes/urls",
    loadChildren: () =>
      import("./refact/pages/components/settings/urls/urls.module").then(
        (m) => m.UrlsModule
      ),
  },
  {
    path: "setup/:codigocrm",
    loadChildren: () =>
      import("./refact/pages/components/settings/setup/setup.module").then(
        (m) => m.SetupModule
      ),
  },
  {
    path: "setup",
    loadChildren: () =>
      import("./refact/pages/components/settings/setup/setup.module").then(
        (m) => m.SetupModule
      ),
  },
  {
    path: "tamanhos",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/tamanhos/tamanhos.module"
      ).then((m) => m.TamanhosModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TAMANHOS },
  },
  {
    path: "cores",
    loadChildren: () =>
      import("./refact/pages/components/retaguarda/cores/cores.module").then(
        (m) => m.CoresModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CORES },
  },
  {
    path: "compradores",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/compradores/compradores.module"
      ).then((m) => m.CompradoresModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.COMPRADORES },
  },
  {
    path: "atributos-pai",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/atributo-pai/atributo-pai.module"
      ).then((m) => m.AtributoPaiModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.ATRIBUTOS_PAI },
  },
  {
    path: "atributos-filho",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/atributo-filho/atributo-filho.module"
      ).then((m) => m.AtributoFilhoModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.ATRIBUTOS_FILHO },
  },
  {
    path: "colecoes",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/colecoes/colecoes.module"
      ).then((m) => m.ColecoesModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.COLECOES },
  },
  {
    path: "linhas",
    loadChildren: () =>
      import("./refact/pages/components/retaguarda/linhas/linhas.module").then(
        (m) => m.LinhasModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.LINHAS },
  },
  {
    path: "materiais",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/materiais/materiais.module"
      ).then((m) => m.MateriaisModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.MATERIAIS },
  },
  {
    path: "modelos",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/modelos/modelos.module"
      ).then((m) => m.ModelosModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.MODELOS },
  },
  {
    path: "cfop",
    loadChildren: () =>
      import("./refact/pages/components/retaguarda/cfop/cfop.module").then(
        (m) => m.CfopModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CFOP },
  },
  {
    path: "conferencia-de-nota",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/conferencia-de-nota/conferencia-de-nota.module"
      ).then((m) => m.ConferenciaDeNotaModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONFERENCIA_NOTA },
  },
  {
    path: "cadastro-de-cartoes",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/cadastro-de-cartoes/cadastro-de-cartoes.module"
      ).then((m) => m.CadastroDeCartoesModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CARTOES },
  },
  {
    path: "conta-corrente",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/conta-corrente/conta-corrente.module"
      ).then((m) => m.ContaCorrenteModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONTA_CORRENTE },
  },
  {
    path: "condicoes-pagamento",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/condicoes-pagamento/condicoes-pagamento.module"
      ).then((m) => m.CondicoesPagamentoModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONDICOES_PAGAMENTO },
  },
  {
    path: "configuracoes-automatizadas",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/configuracao-nfe/configuracao-nfe.module"
      ).then((m) => m.ConfiguracaoNfeModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONFIGURACOES_AUTOMATIZADAS },
  },
  {
    path: "grades",
    loadChildren: () =>
      import("./refact/pages/components/retaguarda/grades/grades.module").then(
        (m) => m.GradesModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.GRADES },
  },
  {
    path: "grupos",
    loadChildren: () =>
      import("./refact/pages/components/retaguarda/grupos/grupos.module").then(
        (m) => m.GruposModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.GRUPOS },
  },
  {
    path: "numero-de-serie",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/numero-de-serie/numero-de-serie.module"
      ).then((m) => m.NumeroDeSerieModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.NUMERO_SERIE },
  },
  {
    path: "certificados",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/certificados/certificados.module"
      ).then((m) => m.CertificadosModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CERTIFICADOS_DIGITAIS },
  },
  {
    path: "motivo-de-troca",
    loadChildren: () =>
      import("./refact/pages/components/motivo-troca/motivo-troca.module").then(
        (m) => m.MotivoTrocaModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.MOTIVO_TROCA },
  },
  // TEMPORARIAMENTE DESATIVADO - Gestor de Vales removido do sistema temporariamente
  // {
  //   path: "vales",
  //   loadChildren: () =>
  //     import(
  //       "./refact/pages/components/gestor-de-vales/gestor-de-vales.module"
  //     ).then((m) => m.GestorDeValesModule),
  //   canActivate: [AuthGuard, PermissionGuard],
  //   data: { permissionIntero: PermissionIntero.GESTOR_VALES },
  // },
  {
    path: "cadastro-de-funcionario",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/cadastro-de-funcionario/cadastro-de-funcionario.module"
      ).then((m) => m.CadastroDeFuncionarioModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.FUNCIONARIOS },
  },
  {
    path: "lojas",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/cadastro-de-lojas/cadastro-de-lojas.module"
      ).then((m) => m.CadastroDeLojasModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.LOJAS },
  },
  {
    path: "ajustes-de-estoque",
    loadChildren: () =>
      import(
        "./refact/pages/components/entrada-saida-avulsa/entrada-saida-avulsa.module"
      ).then((m) => m.EntradaSaidaAvulsaModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permission: PermissionIntero.ENTRADA_SAIDA_AVULSA, permissionIntero: PermissionIntero.ENTRADA_SAIDA_AVULSA },
  },
  {
    path: "cotas",
    loadChildren: () =>
      import("./refact/pages/components/cotas/cotas.module").then(
        (m) => m.CotasModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.COTAS },
  },
  {
    path: "pedido-de-compras",
    loadChildren: () =>
      import(
        "./refact/pages/components/pedido-de-compras/pedido-de-compras.module"
      ).then((m) => m.PedidoDeComprasModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.PEDIDO_COMPRAS },
  },
  {
    path: "produtos",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/produtos/produtos.module"
      ).then((m) => m.ProdutosModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.PRODUTOS },
  },
  {
    path: "transferencias",
    loadChildren: () =>
      import(
        "./refact/pages/components/transferencias/transferencias.module"
      ).then((m) => m.TransferenciasModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TRANSFERENCIAS },
  },
  {
    path: "contadores",
    loadChildren: () =>
      import("./refact/pages/components/contadores/contadores.module").then(
        (m) => m.ContadoresModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONTADORES },
  },
  {
    path: "moedas",
    loadChildren: () =>
      import("./refact/pages/components/moedas/moedas.module").then(
        (m) => m.MoedasModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.MOEDAS },
  },
  {
    path: "cambio",
    loadChildren: () =>
      import("./refact/pages/components/cambio/cambio.module").then(
        (m) => m.CambioModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CAMBIO },
  },
  {
    path: "representantes",
    loadChildren: () =>
      import(
        "./refact/pages/components/representantes/representantes.module"
      ).then((m) => m.RepresentantesModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.REPRESENTANTES },
  },
  {
    path: "notas-fiscais",
    loadChildren: () =>
      import(
        "./refact/pages/components/notas-fiscais/notas-fiscais.module"
      ).then((m) => m.NotasFiscaisModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.NOTAS_FISCAIS },
  },
  {
    path: "usuarios",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/usuarios/usuarios.module"
      ).then((m) => m.UsuariosModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.USUARIOS },
  },
  {
    path: "conferencia",
    loadChildren: () =>
      import("./refact/pages/components/conferencia/conferencia.module").then(
        (m) => m.ConferenciaModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONFERENCIA },
  },
  {
    path: "perfil-de-usuarios",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/perfil-de-usuarios/perfil-de-usuarios.module"
      ).then((m) => m.PerfilDeUsuariosModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.PERFIL_USUARIOS },
  },
  {
    path: "log-lgpd",
    loadChildren: () =>
      import("./refact/pages/components/log-lgpd/log-lgpd.module").then(
        (m) => m.LogLgpdModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.LOG_LGPD },
  },
  {
    path: "pagamentos",
    loadComponent: () =>
      import("./refact/pages/components/pagamentos/pagamentos.component").then(
        (m) => m.PagamentosComponent
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.PAGAMENTOS },
  },
  {
    path: "pagamentos/adicionar",
    loadComponent: () =>
      import(
        "./refact/pages/components/pagamentos/adicionar-editar/adicionar-editar.component"
      ).then((m) => m.AdicionarEditarComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.PAGAMENTOS },
  },
  {
    path: "pagamentos/editar/:id",
    loadComponent: () =>
      import(
        "./refact/pages/components/pagamentos/adicionar-editar/adicionar-editar.component"
      ).then((m) => m.AdicionarEditarComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.PAGAMENTOS },
  },
  {
    path: "recebimentos",
    loadComponent: () =>
      import(
        "./refact/pages/components/recebimentos/recebimentos.component"
      ).then((m) => m.RecebimentosComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.RECEBIMENTOS },
  },
  {
    path: "recebimentos/adicionar",
    loadComponent: () =>
      import(
        "./refact/pages/components/recebimentos/adicionar-editar/adicionar-editar.component"
      ).then((m) => m.AdicionarEditarComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.RECEBIMENTOS },
  },
  {
    path: "recebimentos/editar/:id",
    loadComponent: () =>
      import(
        "./refact/pages/components/recebimentos/adicionar-editar/adicionar-editar.component"
      ).then((m) => m.AdicionarEditarComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.RECEBIMENTOS },
  },
  {
    path: "grupos-financeiros",
    loadChildren: () =>
      import(
        "./refact/pages/components/retaguarda/grupos-financeiros/grupos-financeiros.module"
      ).then((m) => m.GruposFinanceirosModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.GRUPOS_FINANCEIROS },
  },
  {
    path: "classificacoes-financeiras",
    loadComponent: () =>
      import(
        "./refact/pages/components/classificacoes-financeiras/classificacoes-financeiras.component"
      ).then((m) => m.ClassificacoesFinanceirasComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CLASSIFICACOES_FINANCEIRAS },
  },
  {
    path: "classificacoes-financeiras/adicionar",
    loadComponent: () =>
      import(
        "./refact/pages/components/classificacoes-financeiras/adicionar-editar/adicionar-editar.component"
      ).then((m) => m.AdicionarEditarComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CLASSIFICACOES_FINANCEIRAS },
  },
  {
    path: "classificacoes-financeiras/editar/:id",
    loadComponent: () =>
      import(
        "./refact/pages/components/classificacoes-financeiras/adicionar-editar/adicionar-editar.component"
      ).then((m) => m.AdicionarEditarComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CLASSIFICACOES_FINANCEIRAS },
  },
  {
    path: "grupos-contas",
    loadComponent: () =>
      import(
        "./refact/pages/components/grupos-contas/grupos-contas.component"
      ).then((m) => m.GruposContasComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.GRUPOS_CONTAS },
  },
  {
    path: "grupos-contas/adicionar",
    loadComponent: () =>
      import(
        "./refact/pages/components/grupos-contas/adicionar-editar/adicionar-editar.component"
      ).then((m) => m.AdicionarEditarComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.GRUPOS_CONTAS },
  },
  {
    path: "grupos-contas/editar/:id",
    loadComponent: () =>
      import(
        "./refact/pages/components/grupos-contas/adicionar-editar/adicionar-editar.component"
      ).then((m) => m.AdicionarEditarComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.GRUPOS_CONTAS },
  },
  {
    path: "conciliacao-bancaria",
    loadChildren: () =>
      import(
        "./refact/pages/components/conciliacao-bancaria/conciliacao-bancaria-routing"
      ).then((m) => m.ConciliacaoBancariaRoutes),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONCILIACAO_BANCARIA },
  },
  {
    path: "conciliacao-bancaria-automatica",
    loadChildren: () =>
      import(
        "./refact/pages/components/conciliacao-bancaria-automatica/conciliacao-bancaria-automatica-routing"
      ).then((m) => m.ConciliacaoBancariaAutomaticaRoutes),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONCILIACAO_BANCARIA },
  },
  {
    path: "conciliacao-cartao",
    loadChildren: () =>
      import(
        "./refact/pages/components/conciliacao-cartao/conciliacao-cartao-routing"
      ).then((m) => m.ConciliacaoCartaoRoutes),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.CONCILIACAO_CARTOES },
  },
  {
    path: "recebimento-caixa",
    loadChildren: () =>
      import(
        "./refact/pages/components/recebimento-caixa/recebimento-caixa-routing"
      ).then((m) => m.RecebimentoCaixaRoutes),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.RECEBIMENTO_CAIXA },
  },
  {
    path: "aliquotas-iva",
    loadChildren: () =>
      import(
        "./refact/pages/components/aliquotas-iva/aliquotas-iva.module"
      ).then((m) => m.AliquotasIvaModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.ALIQUOTAS_IVA },
  },
  {
    path: "tributo-classificacao",
    loadChildren: () =>
      import(
        "./refact/pages/components/tributo-classificacao/tributo-classificacao.module"
      ).then((m) => m.TributoClassificacaoModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.TRIBUTO_CLASSIFICACAO },
  },
  {
    path: "unidades",
    loadChildren: () =>
      import("./refact/pages/components/unidades/unidades.module").then(
        (m) => m.UnidadesModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.UNIDADES },
  },
  {
    path: "pre-venda",
    loadChildren: () =>
      import("./refact/pages/components/pre-venda/pre-venda.module").then(
        (m) => m.PreVendaModule
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.PRE_VENDA },
  },
  {
    path: "autorizacao-gerencial",
    loadChildren: () =>
      import(
        "./refact/pages/components/autorizacao-gerencial/autorizacao-gerencial.module"
      ).then((m) => m.AutorizacaoGerencialModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.AUTORIZACAO_GERENCIAL },
  },
  {
    path: "demonstrativos-financeiros",
    loadChildren: () =>
      import(
        "./refact/pages/components/demonstrativos-financeiros/demonstrativos-financeiros-routing"
      ).then((m) => m.DemonstrativosFinanceirosRoutes),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionIntero: PermissionIntero.DEMONSTRATIVOS_FINANCEIROS },
  },
  {
    path: "relatorios-financeiros",
    loadComponent: () =>
      import(
        "./refact/pages/components/relatorios-financeiros/relatorios-financeiros.component"
      ).then((m) => m.RelatoriosFinanceirosComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      permission: PermissionIntero.RELATORIO_FINANCEIRO,
      permissionIntero: PermissionIntero.RELATORIO_FINANCEIRO,
    },
  },
  {
    path: "relatorios-estoque",
    loadComponent: () =>
      import(
        "./refact/pages/components/relatorios-estoque/relatorios-estoque.component"
      ).then((m) => m.RelatoriosEstoqueComponent),
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      permission: PermissionIntero.RELATORIO_ESTOQUE,
      permissionIntero: PermissionIntero.RELATORIO_ESTOQUE,
    },
  },
  // Rota pública — redefinição de senha (sem AuthGuard/PermissionGuard)
  {
    path: "reset-senha",
    loadComponent: () =>
      import(
        "./refact/pages/components/reset-senha/reset-senha.component"
      ).then((m) => m.ResetSenhaComponent),
  },
  {
    path: "**",
    component: PageNotFoundComponent,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: SelectivePreloadingStrategy,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
